import { useCallback } from "react"
import { t } from "i18next"
import { useComfortSignatureActivation } from "@/modules/patients/detail/conclude/services/de/useComfortSignatureActivation"
import { useDeSignGeneralAttestationWithTelematikInfrastructureMutation } from "@/modules/patients/detail/general-attestation/de/services/useDeSignGeneralAttestationWithTelematikInfrastructureMutation"
import { useDeSignLegalGuardianWithTelematikInfrastructureMutation } from "@/modules/patients/detail/legal-guardian-attestation/de/services/useDeSignLegalGuardianWithTelematikInfrastructureMutation"
import { useDeSignPrescriptionWithTelematikInfrastructureMutation } from "@/modules/patients/detail/prescription/de/services/useDeSignPrescriptionWithTelematikInfrastructureMutation"
import { ProgressNoteDocument } from "@/modules/patients/detail/progress-note/types/document"
import { useDeSignReferralLetterWithTelematikInfrastructureMutation } from "@/modules/patients/detail/referral/de/services/useDeSignReferralLetterWithTelematikInfrastructureMutation"
import { useDeSignSickLeaveWithTelematikInfrastructureMutation } from "@/modules/patients/detail/sick-leave-certificate/de/services/useDeSignSickLeaveWithTelematikInfrastructureMutation"
import { useDeSignTherapyPrescriptionWithTelematikInfrastructureMutation } from "@/modules/patients/detail/therapy-prescription/de/services/useDeSignTherapyPrescriptionWithTelematikInfrastructureMutation"
import { DE_DOCUMENTS, ProgressNoteDocumentType } from "@/modules/patients/types/ProgressNoteDocument"
import { useConfirmStore } from "@/stores/useConfirmStore"

type TelematikInfrastructureDocumentSigning = {
  variables: {
    id: string
    type: ProgressNoteDocumentType
    progressNoteId: string
  }
  onStart?: () => void
  onPinEntryRequired?: () => void
  onSuccess?: (document: ProgressNoteDocument) => void
  onError?: (error: Error) => void
}

type SigningPayloadError = {
  readonly __typename: string
  readonly message?: string
  readonly errorType?: string | null
}

const COMFORT_SIGNATURE_ACTIVATION_IS_NEEDED = "ComfortSignatureActivationIsNeeded"

// Keyed by the backend error record name carried in SignDocumentMutationFailed.errorType and
// ComfortSignatureActivationResult.errorType; any other failure shows the message as reported by easyTI
const SIGNING_ERROR_MESSAGE_KEY_BY_ERROR_TYPE: Partial<Record<string, string>> = {
  CardTerminalOffline: "patient.conclude.telematikInfrastructureError.cardTerminalOffline",
  ComfortSignatureActivationFailed: "patient.conclude.telematikInfrastructureError.comfortSignatureActivationFailed",
  ComfortSignatureSessionUnavailable:
    "patient.conclude.telematikInfrastructureError.comfortSignatureSessionUnavailable",
  HealthProfessionalCardNotFound: "patient.conclude.telematikInfrastructureError.healthProfessionalCardNotFound",
  HealthProfessionalCardNotInserted: "patient.conclude.telematikInfrastructureError.healthProfessionalCardNotInserted",
  PinEntryTimedOut: "patient.conclude.telematikInfrastructureError.pinEntryTimedOut",
  PinRejected: "patient.conclude.telematikInfrastructureError.pinRejected",
  SigningDocumentUsingTelematikInfrastructureFailed:
    "patient.conclude.telematikInfrastructureError.signingDocumentUsingTelematikInfrastructureFailed",
  TelematikIdMissing: "patient.conclude.telematikInfrastructureError.telematikIdMissing",
  TelematikInfrastructureSigningDisabled:
    "patient.conclude.telematikInfrastructureError.telematikInfrastructureSigningDisabled",
}

const useTelematikInfrastructureDocumentSigning = () => {
  const confirm = useConfirmStore((state) => state.confirm)
  const activateComfortSignature = useComfortSignatureActivation()

  const [signDeSickLeave] = useDeSignSickLeaveWithTelematikInfrastructureMutation()
  const [signDeGeneralAttestation] = useDeSignGeneralAttestationWithTelematikInfrastructureMutation()
  const [signDeLegalGuardian] = useDeSignLegalGuardianWithTelematikInfrastructureMutation()
  const [signDeReferralLetter] = useDeSignReferralLetterWithTelematikInfrastructureMutation()
  const [signDePrescription] = useDeSignPrescriptionWithTelematikInfrastructureMutation()
  const [signDeTherapyPrescription] = useDeSignTherapyPrescriptionWithTelematikInfrastructureMutation()

  const showSigningError = useCallback(
    (errorType: string | null | undefined, message: string) => {
      const messageKey = errorType ? SIGNING_ERROR_MESSAGE_KEY_BY_ERROR_TYPE[errorType] : undefined
      // The dialog translates string content, so a verbatim easyTI message is wrapped to be shown as is
      confirm({
        title: t("patient.conclude.unableToSignDocumentWithTelematikInfrastructure"),
        content: messageKey ?? <span>{message}</span>,
        cancelText: "common.button.close",
        isShowConfirmButton: false,
      })
    },
    [confirm],
  )

  const signDocumentWithTelematikInfrastructure = useCallback(
    async ({
      variables: { id, type, progressNoteId },
      onStart,
      onPinEntryRequired,
      onSuccess,
      onError,
    }: TelematikInfrastructureDocumentSigning) => {
      onStart?.()

      const handleFailure = (errorType: string | null | undefined, message: string) => {
        showSigningError(errorType, message)
        onError?.(new Error(message))
      }

      const handleError = (error: Error) => {
        handleFailure(undefined, error.message)
      }

      const handleSuccess = (document: Partial<ProgressNoteDocument>) => {
        if (document.id) {
          onSuccess?.({
            id: document.id,
            progressNoteId,
            type,
            signedAt: document.signedAt,
            signedBy: document.signedBy,
            fileId: document.fileId,
          })
        }
      }

      const run = (isRetryAfterActivation: boolean) => {
        const handleCompleted = (
          errors: ReadonlyArray<SigningPayloadError> | null | undefined,
          document: Partial<ProgressNoteDocument>,
        ) => {
          const activationIsNeededError = errors?.find(
            (payloadError) => payloadError.__typename === COMFORT_SIGNATURE_ACTIVATION_IS_NEEDED,
          )
          if (!activationIsNeededError) {
            const signingFailure = errors?.[0]
            if (signingFailure) {
              handleFailure(signingFailure.errorType, signingFailure.message ?? t("patient.dialog.error.tryAgainLater"))
            } else {
              handleSuccess(document)
            }
            return
          }

          if (isRetryAfterActivation) {
            // The comfort signature was just activated but signing still reports it as inactive:
            // surface the error instead of activating again to rule out an endless loop
            handleFailure(undefined, activationIsNeededError.message ?? "")
            return
          }

          onPinEntryRequired?.()
          activateComfortSignature({
            onActivated: () => run(true),
            onFailed: ({ errorType, message }) =>
              handleFailure(errorType, message ?? t("patient.dialog.error.tryAgainLater")),
            onError: handleError,
          })
        }

        // In general we should be exhaustive but enabling it here would bring LOT of additional types, maybe something should be revisited later...
        // oxlint-disable-next-line typescript/switch-exhaustiveness-check
        switch (type) {
          case DE_DOCUMENTS.SICK_LEAVE:
            signDeSickLeave({
              id,
              onCompleted(response) {
                handleCompleted(
                  response.de_signSickLeaveDocumentWithTelematikInfrastructure.errors,
                  response.de_signSickLeaveDocumentWithTelematikInfrastructure.de_SickLeave || {},
                )
              },
              onError: handleError,
            })
            break
          case DE_DOCUMENTS.GENERAL_ATTESTATION:
            signDeGeneralAttestation({
              id,
              onCompleted(response) {
                handleCompleted(
                  response.de_signGeneralAttestationDocumentWithTelematikInfrastructure.errors,
                  response.de_signGeneralAttestationDocumentWithTelematikInfrastructure.de_GeneralAttestation || {},
                )
              },
              onError: handleError,
            })
            break
          case DE_DOCUMENTS.GUARDIAN_ATTESTATION:
            signDeLegalGuardian({
              id,
              onCompleted(response) {
                handleCompleted(
                  response.de_signAttestationGuardianDocumentWithTelematikInfrastructure.errors,
                  response.de_signAttestationGuardianDocumentWithTelematikInfrastructure.de_AttestationGuardian || {},
                )
              },
              onError: handleError,
            })
            break
          case DE_DOCUMENTS.REFERRAL_LETTER:
            signDeReferralLetter({
              id,
              onCompleted(response) {
                handleCompleted(
                  response.de_signReferralLetterDocumentWithTelematikInfrastructure.errors,
                  response.de_signReferralLetterDocumentWithTelematikInfrastructure.de_ReferralLetter || {},
                )
              },
              onError: handleError,
            })
            break
          case DE_DOCUMENTS.PRESCRIPTION:
            signDePrescription({
              id,
              onCompleted(response) {
                handleCompleted(
                  response.de_signPrescriptionDocumentWithTelematikInfrastructure.errors,
                  response.de_signPrescriptionDocumentWithTelematikInfrastructure.de_Prescription || {},
                )
              },
              onError: handleError,
            })
            break
          case DE_DOCUMENTS.THERAPY_PRESCRIPTION:
            signDeTherapyPrescription({
              id,
              onCompleted(response) {
                handleCompleted(
                  response.de_signTherapyPrescriptionDocumentWithTelematikInfrastructure.errors,
                  response.de_signTherapyPrescriptionDocumentWithTelematikInfrastructure.de_TherapyPrescription || {},
                )
              },
              onError: handleError,
            })
            break
          default:
            break
        }
      }

      run(false)
    },
    [
      showSigningError,
      activateComfortSignature,
      signDeSickLeave,
      signDeGeneralAttestation,
      signDeLegalGuardian,
      signDeReferralLetter,
      signDePrescription,
      signDeTherapyPrescription,
    ],
  )

  return signDocumentWithTelematikInfrastructure
}

export { useTelematikInfrastructureDocumentSigning }
